import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Globe, Check, ChevronsUpDown, TrendingUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAllYfiCountries } from "@/hooks/useYfiCountries";
import 'flag-icons/css/flag-icons.min.css';
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCountryName } from "@/hooks/useCountryName";

interface BenchmarkStepProps {
  focusCountry: string;
  onFocusCountryChange: (value: string) => void;
  onComplete: () => void;
  onBack: () => void;
}

const GLOBAL_YFI_AVERAGE = 58.5;

export function BenchmarkStep({
  focusCountry,
  onFocusCountryChange,
  onComplete,
  onBack,
}: BenchmarkStepProps) {
  const { t } = useTranslation();
  const { getCountryName } = useCountryName();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch countries from database
  const { countries, countryFlagCodes, isLoading } = useAllYfiCountries();

  // Get countries with complete data
  const countriesWithData = useMemo(
    () => countries
      .filter(c => c.data_availability === 'Complete')
      .map(c => c.country_name)
      .sort(),
    [countries]
  );

  const filteredCountries = useMemo(() => {
    if (!search) return countriesWithData;
    return countriesWithData.filter((c) =>
      c.toLowerCase().includes(search.toLowerCase()) ||
      getCountryName(c).toLowerCase().includes(search.toLowerCase())
    );
  }, [search, countriesWithData, getCountryName]);

  const selectedCountryData = useMemo(() => {
    if (!focusCountry) return null;
    return countries.find((c) => c.country_name === focusCountry);
  }, [focusCountry, countries]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <div className="mx-auto w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
          <TrendingUp className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{t('onboarding.benchmark.title')}</h2>
        <p className="text-muted-foreground">{t('onboarding.benchmark.description')}</p>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            {t('onboarding.benchmark.focusCountry')}
          </Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between bg-background"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.loading')}
                  </span>
                ) : focusCountry ? (
                  <span className="flex items-center gap-2">
                    <span className={`fi fi-${countryFlagCodes[focusCountry] || 'un'} w-5 h-4 rounded shadow-sm`} />
                    <span>{getCountryName(focusCountry)}</span>
                  </span>
                ) : (
                  t('onboarding.benchmark.focusCountryPlaceholder')
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 z-50" align="start">
              <Command>
                <CommandInput
                  placeholder={t('onboarding.benchmark.searchCountry')}
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>{t('onboarding.benchmark.noCountryFound')}</CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-auto">
                    {filteredCountries.map((country) => {
                      const localizedName = getCountryName(country);
                      const showEnglish = localizedName !== country;
                      return (
                        <CommandItem
                          key={country}
                          value={country}
                          onSelect={() => {
                            onFocusCountryChange(country);
                            setOpen(false);
                            setSearch("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              focusCountry === country ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className={`fi fi-${countryFlagCodes[country] || 'un'} w-5 h-4 rounded shadow-sm mr-2`} />
                          <span>{localizedName}</span>
                          {showEnglish && (
                            <span className="ml-1 text-muted-foreground">({country})</span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Mini comparison chart */}
        {selectedCountryData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-xl bg-card border border-border space-y-4"
          >
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <span className={`fi fi-${countryFlagCodes[focusCountry] || 'un'} w-5 h-4 rounded shadow-sm`} />
              {t('onboarding.benchmark.vsGlobal', { country: getCountryName(focusCountry) })}
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{getCountryName(focusCountry)}</span>
                  <span className="font-semibold text-primary">{selectedCountryData.yfi_score.toFixed(1)}</span>
                </div>
                <Progress value={selectedCountryData.yfi_score} className="h-2" />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('onboarding.benchmark.globalAverage')}</span>
                  <span className="font-semibold text-muted-foreground">{GLOBAL_YFI_AVERAGE}</span>
                </div>
                <Progress value={GLOBAL_YFI_AVERAGE} className="h-2 opacity-50" />
              </div>

              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  {selectedCountryData.yfi_score > GLOBAL_YFI_AVERAGE ? (
                    <span className="text-success">
                      {t('onboarding.benchmark.aboveAverage', { value: (selectedCountryData.yfi_score - GLOBAL_YFI_AVERAGE).toFixed(1) })}
                    </span>
                  ) : (
                    <span className="text-destructive">
                      {t('onboarding.benchmark.belowAverage', { value: (GLOBAL_YFI_AVERAGE - selectedCountryData.yfi_score).toFixed(1) })}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          {t('onboarding.back')}
        </Button>
        <Button onClick={onComplete} disabled={!focusCountry || isLoading} className="flex-1">
          {t('onboarding.completeSetup')}
        </Button>
      </div>
    </motion.div>
  );
}
